import React, { useState, useEffect } from 'react';
import {
  ArrowRight, Calendar, Clock, User, ChevronRight,
  Search, BookOpen, Star, Sparkles, MoveRight
} from 'lucide-react';
import blogHeaderImage from '../assets/Blog.png';
import SEO from './SEO';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const categories = ['All', 'Web Development', 'Mobile Apps', 'Digital Marketing', 'SEO & Ads', 'Software Dev', 'Design & Video'];

  const blogPosts = [
    {
      id: 1,
      title: "Modern Website Development: Building Fast & Secure Experiences",
      excerpt: "Explore the cutting-edge frameworks and practices to build highly responsive, fast, and secure websites tailored to your brand.",
      category: "Web Development",
      author: "Alex Morgan",
      date: "Jun 15, 2026",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80",
      featured: true
    },
    {
      id: 2,
      title: "Data-Driven Digital Marketing Strategies for 2026",
      excerpt: "Learn how to accelerate your business growth through engaging social media campaigns and data-driven marketing strategies.",
      category: "Digital Marketing",
      author: "Sarah Chen",
      date: "Jun 12, 2026",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
      featured: false
    },
    {
      id: 3,
      title: "Mastering Google & Meta Ads for Maximum ROI",
      excerpt: "A comprehensive guide on optimizing your digital presence to rank higher and running targeted ad campaigns designed for maximum lead generation.",
      category: "SEO & Ads",
      author: "David Russo",
      date: "Jun 08, 2026",
      readTime: "10 min read",
      image: "https://images.unsplash.com/photo-1533750516457-a7f992034fec?auto=format&fit=crop&w=800&q=80",
      featured: false
    },
    {
      id: 4,
      title: "iOS vs Android: Creating Intuitive Mobile Applications",
      excerpt: "Discover the differences between native platforms and cross-platform frameworks to build custom mobile applications with seamless user experiences.",
      category: "Mobile Apps",
      author: "Elena Rodriguez",
      date: "Jun 05, 2026",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80",
      featured: false
    },
    {
      id: 5,
      title: "Custom Software Solutions: Scaling Enterprise Workflows",
      excerpt: "How bespoke, scalable enterprise software solutions can eliminate operational bottlenecks and be tailored to your unique workflows.",
      category: "Software Dev",
      author: "James Wilson",
      date: "May 28, 2026",
      readTime: "12 min read",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80",
      featured: false
    },
    {
      id: 6,
      title: "The Power of Visual Branding: Graphic Design & Video Creation",
      excerpt: "Boost your brand identity with stunning visual designs, creative branding, and high-quality video production for your business.",
      category: "Design & Video",
      author: "Priya Sharma",
      date: "May 20, 2026",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=800&q=80",
      featured: false
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts.find(post => post.featured) || filteredPosts[0];
  const regularPosts = filteredPosts.filter(post => post.id !== (featuredPost?.id || -1));

  const gradientText = "bg-gradient-to-r from-[#0EA5E9] to-[#0B2B5E] bg-clip-text text-transparent";

  return (
    <div className="min-h-screen bg-[#0A0F1F] text-white overflow-x-hidden selection:bg-[#0EA5E9] selection:text-white pt-24 pb-20 lg:pt-20" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Digital Marketing & Tech Blog – Vishwa DigiTech"
        description="Read the latest articles on web development, digital marketing, SEO, social media marketing, app development & more by Vishwa DigiTech experts. Stay updated with industry trends."
        keywords="digital marketing blog india, web development articles, SEO tips india, social media marketing blog, tech blog sangli, vishwa digitech blog"
        canonicalPath="/blogs"
      />

      {/* Aurora Background Effects */}
      <div className="absolute top-0 left-0 w-full h-[80vh] overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[#0EA5E9] opacity-20 blur-[150px] mix-blend-screen animate-[pulse_6s_infinite]" />
        <div className="absolute top-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[#0B2B5E] opacity-40 blur-[120px] mix-blend-screen animate-[pulse_8s_infinite_reverse]" />
      </div>

      {/* Hero Section (Matches AboutUs Layout) */}
      <div className={`relative w-full overflow-hidden mb-24 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} border-b border-white/10`}>
        <img src={blogHeaderImage} alt="Blog Header" className="absolute inset-0 w-full h-full object-cover object-center" />
        {/* <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1F] via-[#0A0F1F]/95 to-[#0A0F1F]/40 md:to-transparent"></div> */}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="py-24 md:py-36 lg:py-48 max-w-3xl">
            <div className={`transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="inline-block px-5 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-6 backdrop-blur-md shadow-lg">
                Our Journal
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-[#0EA5E9] mb-6 tracking-tight">
                BLOG
              </h1>
              <p className="text-gray-300 text-lg md:text-xl leading-relaxed font-light">
                Discover the latest insights, strategies, and technology trends to help <br /> your business stay ahead in the digital world.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Sleek Floating Filter Dock */}
        <div className={`flex justify-center mb-16 transition-all duration-1000 delay-200 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-flex overflow-x-auto hide-scrollbar items-center p-1.5 bg-[#0A0F1F]/60 backdrop-blur-2xl border border-white/10 rounded-full shadow-2xl">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`whitespace-nowrap px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 ${activeCategory === category
                  ? 'bg-gradient-to-r from-[#0EA5E9] to-[#0284c7] text-white shadow-[0_0_15px_rgba(14,165,233,0.3)]'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Premium Split-Card Featured Post */}
        {featuredPost && (
          <div className={`mb-24 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="group relative bg-[#0F1629]/80 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden flex flex-col lg:flex-row shadow-[0_0_50px_rgba(0,0,0,0.5)] cursor-pointer">

              {/* Content Side (Left) */}
              <div className="w-full lg:w-1/2 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative z-10">
                {/* Subtle background glow behind text */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#0EA5E9]/10 to-transparent -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="flex items-center gap-4 mb-6">
                  <span className="px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#0EA5E9] bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 rounded-full">
                    Featured Insight
                  </span>
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">{featuredPost.category}</span>
                </div>

                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white group-hover:text-[#0EA5E9] transition-colors duration-500 leading-tight tracking-tight">
                  {featuredPost.title}
                </h2>

                <p className="text-gray-400 text-lg mb-10 leading-relaxed font-light">
                  {featuredPost.excerpt}
                </p>

                <div className="flex items-center justify-between mt-auto pt-8 border-t border-white/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-300" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{featuredPost.author}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        {featuredPost.date} <span className="w-1 h-1 rounded-full bg-gray-600"></span> {featuredPost.readTime}
                      </p>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-300 transform group-hover:-rotate-45">
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </div>

              {/* Image Side (Right) */}
              <div className="w-full lg:w-1/2 relative min-h-[300px] lg:min-h-[500px]">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0F1629]/80 via-[#0F1629]/30 to-transparent z-10 hidden lg:block" />
                <div className="absolute inset-0 bg-[#0EA5E9]/20 mix-blend-overlay z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000"
                />
              </div>
            </div>
          </div>
        )}

        {/* Editorial Alternating Layout for Regular Posts */}
        <div className="space-y-24">
          {regularPosts.map((post, index) => {
            // Determine layout style based on index
            const isEven = index % 2 === 0;

            return (
              <div
                key={post.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center group cursor-pointer`}
              >
                {/* Image Section */}
                <div className="w-full lg:w-1/2 relative rounded-[2rem] overflow-hidden aspect-[4/3]">
                  <div className="absolute inset-0 bg-[#0EA5E9]/20 mix-blend-overlay z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                  />
                  {/* Image Border Glow effect */}
                  <div className="absolute inset-0 border border-white/10 rounded-[2rem] group-hover:border-[#0EA5E9]/50 transition-colors duration-500 z-20" />
                </div>

                {/* Content Section */}
                <div className="w-full lg:w-1/2 flex flex-col justify-center">
                  <div className="flex items-center gap-4 text-xs font-bold text-gray-500 uppercase tracking-widest mb-6">
                    <span className="text-[#0EA5E9]">{post.category}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-extrabold mb-6 text-white group-hover:text-[#0EA5E9] transition-colors duration-500 leading-tight">
                    {post.title}
                  </h3>

                  <p className="text-gray-400 text-lg mb-8 leading-relaxed font-light">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-8 border-t border-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{post.author}</p>
                        <p className="text-xs text-gray-500">{post.readTime}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm font-bold text-white group-hover:text-[#0EA5E9] transition-colors uppercase tracking-widest">
                      <span className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">Read</span>
                      <MoveRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-500" />
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-32 bg-white/5 backdrop-blur-sm border border-white/10 rounded-[3rem] mt-12">
            <div className="w-24 h-24 mx-auto bg-white/5 rounded-full flex items-center justify-center mb-8">
              <Search className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-3xl font-extrabold text-white mb-4">No insights found</h3>
            <p className="text-gray-400 mb-10 max-w-md mx-auto text-lg">We couldn't find any articles matching your current filters. Try exploring other categories.</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
              className="px-10 py-4 rounded-full bg-[#0EA5E9] text-white font-bold tracking-wide hover:scale-105 hover:shadow-[0_0_30px_rgba(14,165,233,0.4)] transition-all duration-300"
            >
              Reset Search
            </button>
          </div>
        )}

      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default Blog;
